import { saveAs } from 'file-saver'
import JsPDF from 'jspdf'
import 'jspdf-autotable'
import PropTypes from 'prop-types'
import { useLocation } from 'wouter'
import * as XLSX from 'xlsx'

export default function TableActionBar({
  tableName,
  createNewItemURL,
  datos,
  rows,
  columns,
  setAllFilters
}) {
  const [, setLocation] = useLocation()

  // Función para exportar registros en Excel:
  const handleExcel = () => {
    // Mostrar en Excel solo las columnas visibles en la tabla
    const validAccessors = columns.map((col) => col.accessor).filter(Boolean)
    const filteredDatos = datos.map((row) => {
      return Object.keys(row)
        .filter((key) => validAccessors.includes(key))
        .reduce((acc, key) => {
          acc[key] = row[key]
          return acc
        }, {})
    })

    const worksheet = XLSX.utils.json_to_sheet(filteredDatos)

    // Crear dinámicamente los encabezados basados en la propiedad 'columns'
    const newHeaders = columns
      .filter((col) => col.Header && col.Header !== '')
      .map((col) => col.Header)
    worksheet['!cols'] = newHeaders.map(() => ({ wch: 20 })) // Ajustar el ancho de las columnas

    // Reemplazar los encabezados en la hoja con los nuevos encabezados dinámicos
    newHeaders.forEach((header, index) => {
      worksheet[XLSX.utils.encode_cell({ r: 0, c: index })].v = header
    })

    const workbook = {
      Sheets: { data: worksheet },
      SheetNames: ['data']
    }
    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array'
    })
    const data = new Blob([excelBuffer], { type: 'application/octet-stream' })

    const fechaPDF = new Date()
    const year = fechaPDF.getFullYear()
    const month = (fechaPDF.getMonth() + 1).toString().padStart(2, '0')
    const day = fechaPDF.getDate().toString().padStart(2, '0')
    const hours = fechaPDF.getHours().toString().padStart(2, '0')
    const minutes = fechaPDF.getMinutes().toString().padStart(2, '0')
    const seconds = fechaPDF.getSeconds().toString().padStart(2, '0')

    saveAs(
      data,
      `${tableName}_${year}${month}${day}${hours}${minutes}${seconds}.xlsx`
    )
  }

  // Función para exportar registros PDF:
  const handlePDF = () => {
    const doc = new JsPDF()

    // Crear dinámicamente los encabezados y los datos basados en las columnas
    const head = columns
      .filter((col) => col.Header && col.Header !== '')
      .map((col) => col.Header) // Extraer los encabezados de las columnas
    const body = datos.map(
      (dato) => columns.map((col) => dato[col.accessor]) // Extraer los datos de las filas según el 'accessor' de las columnas
    )

    doc.autoTable({
      head: [head],
      body,
      styles: {
        fontSize: 8,
        cellPadding: 1,
        lineWidth: 0.1,
        lineColor: [0, 111, 83]
      },
      // Estilos dinámicos para las columnas
      // columnStyles: columns.reduce((styles, col, index) => {
      //   styles[index] = { cellWidth: 18 } // Puedes ajustar esto según el contenido de las columnas
      //   return styles
      // }, {}),
      margin: { top: 20, right: 10, bottom: 20, left: 10 },
      pageBreak: 'auto',
      rowPageBreak: 'avoid',
      headStyles: {
        fillColor: [0, 111, 83],
        lineWidth: 0.1,
        lineColor: [0, 111, 83]
      },
      alternateRowStyles: { fillColor: [221, 221, 221] },
      showHead: 'everyPage',
      showFoot: 'everyPage',
      didDrawPage: (data) => {
        const pageWidth = doc.internal.pageSize.width
        const pageHeight = doc.internal.pageSize.height

        // Contenido del pie de página (número de página)
        doc.setFontSize(10)
        doc.setFont('helvetica', 'bold')
        doc.text(`${data.pageNumber}`, pageWidth / 2, pageHeight - 10, {
          align: 'center'
        })

        // Contenido del encabezado - replicando el diseño del logo
        const headerText = 'Aj. Santa Margarida i els Monjos' // Primera parte "Mi"

        const space = 18
        doc.setFontSize(24)

        // Primera parte del texto ("Mi") con color #0b1f92
        doc.setTextColor(0, 111, 83) // RGB para #0b1f92
        const textWidth = doc.getTextWidth(headerText)
        doc.text(headerText, (pageWidth - textWidth) / 2, space)
      }
    })

    const fechaPDF = new Date()
    const year = fechaPDF.getFullYear()
    const month = (fechaPDF.getMonth() + 1).toString().padStart(2, '0')
    const day = fechaPDF.getDate().toString().padStart(2, '0')
    const hours = fechaPDF.getHours().toString().padStart(2, '0')
    const minutes = fechaPDF.getMinutes().toString().padStart(2, '0')
    const seconds = fechaPDF.getSeconds().toString().padStart(2, '0')

    doc.save(
      `${tableName}_${year}${month}${day}${hours}${minutes}${seconds}.pdf`
    )
  }

  return (
    <>
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="flex items-center gap-x-3">
          <h2 className="text-lg font-medium text-gray-800 dark:text-white">
            {tableName}
          </h2>

          <span className="px-3 py-1 text-xs text-white bg-[#006F53] rounded-full font-medium dark:bg-gray-800 dark:text-blue-400">
            {rows?.length} registres
          </span>
        </div>
      </div>

      <div className="flex flex-col-reverse items-center justify-between mt-4 sm:mt-5 sm:flex-row gap-x-4">
        <div className="flex items-center w-full mt-5 sm:w-fit gap-x-4 sm:mt-0">
          <button
            className="w-1/2 px-5 py-2 text-sm text-white transition-colors duration-200 bg-[#006F53] border border-[#006F53] rounded-md sm:w-36 dark:hover:bg-gray-800 enabled:hover:bg-[#e8f5f0] enabled:hover:text-[#006F53] dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-20 focus:outline-none focus:ring focus:ring-[#006F53] focus:border-[#006F53] dark:focus:border-[#006F53]"
            onClick={handleExcel}
            disabled={rows?.length === 0}
          >
            Exportar Excel
          </button>

          <button
            className="w-1/2 px-5 py-2 text-sm text-white transition-colors duration-200 bg-[#006F53] border border-[#006F53] rounded-md sm:w-36 dark:hover:bg-gray-800 enabled:hover:bg-[#e8f5f0] enabled:hover:text-[#006F53] dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-20 focus:outline-none focus:ring focus:ring-[#006F53] focus:border-[#006F53] dark:focus:border-[#006F53]"
            onClick={handlePDF}
            disabled={rows?.length === 0}
          >
            Exportar PDF
          </button>
        </div>

        <div className="flex items-center w-full sm:w-fit gap-x-4">
          <button
            className="w-1/2 px-5 py-2 text-sm text-white transition-colors duration-200 bg-[#006F53] border border-[#006F53] rounded-md sm:w-36 dark:hover:bg-gray-800 dark:bg-gray-900 hover:bg-[#e8f5f0] hover:text-[#006F53] dark:text-white dark:border-gray-700 focus:ring-opacity-20 focus:outline-none focus:ring focus:ring-[#006F53] focus:border-[#006F53] dark:focus:border-[#006F53]"
            onClick={() => {
              setAllFilters([])

              columns.forEach((column) => {
                localStorage.setItem(column.accessor, undefined)
              })
            }}
          >
            Borrar filtres
          </button>

          <button
            type="button"
            className="w-1/2 text-center px-5 py-2 text-sm text-white transition-colors duration-200 bg-[#006F53] border border-[#006F53] rounded-md sm:w-36 dark:hover:bg-gray-800 enabled:hover:bg-[#e8f5f0] enabled:hover:text-[#006F53] dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-20 focus:outline-none focus:ring focus:ring-[#006F53] focus:border-[#006F53] dark:focus:border-[#006F53]"
            onClick={() => setLocation(createNewItemURL)}
          >
            Crear
          </button>
        </div>
      </div>
    </>
  )
}

// Define los tipos de las props que recibe el componente actual
TableActionBar.propTypes = {
  tableName: PropTypes.string.isRequired,
  createNewItemURL: PropTypes.string.isRequired,
  datos: PropTypes.array,
  rows: PropTypes.any,
  columns: PropTypes.any,
  setAllFilters: PropTypes.func
}
